import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cPath } from "../../constants";
import { logoutUser } from "../../store/actions/authActions";
import SearchModal from "./SearchModal";
import NavbarProfileMenu from "./NavbarProfileMenu";
import LanguageSwitcher from "../common/LanguageSwitcher";
import {
  NavRoot,
  NavInner,
  NavBrandSlot,
  NavCenterSlot,
  Brand,
  BrandAccent,
  BrandRest,
  DesktopLinks,
  NavLinkStyled,
  RightCluster,
  GhostLink,
  JoinBtn,
  IconBtn,
  MobilePanel,
  MobileLink,
  MobileAccent,
  MobileDivider,
  NavSearchBtn,
  MobileSearchTrigger,
  MobilePlainButton,
} from "./Navbar.styles";

export default function Navbar() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);

  const closeSearch = useCallback(() => setSearchModalOpen(false), []);

  const openSearch = useCallback(() => {
    setSearchModalOpen(true);
    setMobileMenuOpen(false);
  }, []);

  const closeMobile = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <>
      <NavRoot>
        <NavInner>
          <NavBrandSlot>
            <Brand to={cPath("")}>
              <BrandAccent>DEAL</BrandAccent>
              <BrandRest>BEEZ</BrandRest>
            </Brand>
          </NavBrandSlot>
          <NavCenterSlot>
            <DesktopLinks>
              <NavLinkStyled to={cPath("coupons")}>{t("nav.coupons")}</NavLinkStyled>
              <NavLinkStyled to={cPath("stores")}>{t("nav.stores")}</NavLinkStyled>
              <NavLinkStyled to={cPath("categories")}>{t("nav.categories")}</NavLinkStyled>
              <NavLinkStyled to={cPath("blog")}>{t("nav.savingTips")}</NavLinkStyled>
              <NavLinkStyled to={cPath("about")}>{t("nav.about")}</NavLinkStyled>
              <NavLinkStyled to={cPath("submit-deal")}>{t("nav.submitDeal")}</NavLinkStyled>
            </DesktopLinks>
          </NavCenterSlot>
          <RightCluster>
            <NavSearchBtn
              type="button"
              aria-label={t("nav.searchAriaLabel")}
              onClick={() => setSearchModalOpen(true)}
            >
              <Search size={20} strokeWidth={2} />
            </NavSearchBtn>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <NavbarProfileMenu user={user} />
            ) : (
              <>
                <GhostLink to={cPath("signin")}>{t("nav.signIn")}</GhostLink>
                <JoinBtn to={cPath("register")}>{t("nav.join")}</JoinBtn>
              </>
            )}
            <IconBtn
              type="button"
              aria-label={mobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={24} strokeWidth={2} />
              ) : (
                <Menu size={24} strokeWidth={2} />
              )}
            </IconBtn>
          </RightCluster>
        </NavInner>
        {mobileMenuOpen && (
          <MobilePanel>
            <MobileLink to={cPath("coupons")} onClick={closeMobile}>{t("nav.coupons")}</MobileLink>
            <MobileLink to={cPath("stores")} onClick={closeMobile}>{t("nav.stores")}</MobileLink>
            <MobileLink to={cPath("categories")} onClick={closeMobile}>{t("nav.categories")}</MobileLink>
            <MobileLink to={cPath("blog")} onClick={closeMobile}>{t("nav.savingTips")}</MobileLink>
            <MobileLink to={cPath("about")} onClick={closeMobile}>{t("nav.about")}</MobileLink>
            <MobileAccent to={cPath("submit-deal")} onClick={closeMobile}>{t("nav.submitDeal")}</MobileAccent>
            <MobileDivider>
              {isAuthenticated ? (
                <>
                  {user?.name ? (
                    <span
                      style={{
                        display: "block",
                        fontSize: "0.8125rem",
                        color: "hsl(220 10% 46%)",
                        marginBottom: "0.35rem",
                      }}
                    >
                      {user.name}
                    </span>
                  ) : null}
                  <MobileLink to={cPath("dashboard")} onClick={closeMobile}>{t("nav.dashboard")}</MobileLink>
                  <MobilePlainButton
                    type="button"
                    onClick={() => {
                      dispatch(logoutUser());
                      closeMobile();
                    }}
                  >
                    {t("nav.signOut")}
                  </MobilePlainButton>
                </>
              ) : (
                <>
                  <MobileLink to={cPath("signin")} onClick={closeMobile}>{t("nav.signIn")}</MobileLink>
                  <MobileLink to={cPath("register")} onClick={closeMobile}>{t("nav.join")}</MobileLink>
                </>
              )}
            </MobileDivider>
            <MobileSearchTrigger type="button" onClick={openSearch}>
              <Search size={18} strokeWidth={2} />
              {t("nav.searchStores")}
            </MobileSearchTrigger>
          </MobilePanel>
        )}
      </NavRoot>
      <SearchModal isOpen={searchModalOpen} onClose={closeSearch} />
    </>
  );
}
